import { Observable } from 'rxjs';
import { ApiEndpointConfiguration, EmptyResponse, IMobizHttpService, MobizServerApi } from '../../core';
import { Inject, Injectable } from '../../injection';

import { MobizSettingsValue } from '../data/mobiz-settings-value.do';
import { MobizSettings } from '../data/mobiz-settings.do';
import { IMobizSettingsService } from '../interfaces/mobiz-settings.interface';
import { MobizSettingsScope } from '../other/mobiz-settings-scope.type';
import { MobizSettingsKey } from '../other/mobiz-settings.type';

@Injectable()
export class MobizSettingsService implements IMobizSettingsService {
  constructor(
    @Inject(IMobizHttpService)
    protected http: IMobizHttpService,
  ) {
  }

  /**
   * @inheritdoc
   */
  public add<T extends MobizSettingsValue, K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
    instance: MobizSettings<T>,
  ): Observable<MobizSettings<T>> {
    return this.save(scope, scopeId, key, instance);
  }

  /**
   * @inheritdoc
   */
  public delete<K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
  ): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.Settings,
      urlParameters: {
        scope: scope,
        scopeId: scopeId,
        key: key,
      },
    };

    return this.http.delete(new EmptyResponse(), config);
  }

  /**
   * @inheritdoc
   */
  public get<T extends MobizSettingsValue, K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
    instance: MobizSettings<T>,
  ): Observable<MobizSettings<T>> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.Settings,
      urlParameters: {
        scope: scope,
        scopeId: scopeId,
        key: key,
      },
    };

    return this.http.get(instance, config);
  }

  /**
   * @inheritdoc
   */
  public save<T extends MobizSettingsValue, K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
    instance: MobizSettings<T>,
  ): Observable<MobizSettings<T>> {
    const config: ApiEndpointConfiguration = {
      body: JSON.stringify(instance),
      serverApi: MobizServerApi.Settings,
      urlParameters: {
        scope: scope,
        scopeId: scopeId,
        key: key,
      },
    };

    return instance.origin === 'DB'
      ? this.http.put(instance, config)
      : this.http.post(instance, config);
  }

  /**
   * @inheritdoc
   */
  public update<T extends MobizSettingsValue, K extends string = MobizSettingsKey>(
    scope: MobizSettingsScope,
    scopeId: string,
    key: K,
    instance: MobizSettings<T>,
  ): Observable<MobizSettings<T>> {
    return this.save(scope, scopeId, key, instance);
  }

}
